/**
 * escPosBarcode.ts — Primitivas ESC/POS de códigos de barras (compartido).
 *
 * QUÉ HACE:
 *  - Genera los bytes ESC/POS de un código de barras CODE128 (subconjunto B,
 *    `GS k 73`), usados tanto por la etiqueta de producto (inventario) como
 *    por la etiqueta de pesaje (caja). Un solo lugar = mismo bytecode.
 *  - Genera QR nativo de la térmica (`GS ( k` fn 49, modelo 2) — no depende
 *    del DOM/canvas, imprime más grande y en cualquier térmica Epson-compatible.
 *
 * QUÉ EXPORTA:
 *  - buildCode128EscPosBytes(code, opts?) → Uint8Array
 *  - buildQrCodeEscPos(text, moduleSize?, eccLevel?) → Uint8Array
 *
 * QUÉ NO DEBE HACER:
 *  - No conoce de productos, etiquetas ni impresoras (solo bytes).
 *  - No genera raster (eso vive en imageDataUrlToEscPosRaster).
 */

const GS = 0x1d;

export interface Code128EscPosOptions {
  /** Alto del código en dots (por defecto 64). */
  height?: number;
  /** Ancho de barra (GS w, por defecto 2). */
  barWidth?: number;
  /** 0 = sin texto, 1 = arriba, 2 = abajo (por defecto 2). */
  textPosition?: 0 | 1 | 2;
  /**
   * Subconjunto CODE128: "B" (alfanumérico, por defecto) o "C" (SOLO dígitos,
   * ~la mitad del ancho: cada símbolo de 11 módulos codifica 2 dígitos, como
   * el de las básculas comerciales). Si la cantidad de dígitos es impar se
   * prefija "0" (el decodificador lo ignora).
   */
  subset?: "B" | "C";
}

/**
 * Comando ESC/POS completo para CODE128:
 * GS w (ancho) + GS h (alto) + GS H (texto) + GS k 73 n {B|C data.
 */
export function buildCode128EscPosBytes(
  code: string,
  opts: Code128EscPosOptions = {},
): Uint8Array {
  const { height = 64, barWidth = 2, textPosition = 2, subset = "B" } = opts;

  let data: Uint8Array;
  if (subset === "C") {
    // Subconjunto C: cada byte = 2 dígitos (0-99). Requiere cantidad par de
    // dígitos; si es impar se prefija "0" (los códigos ALTENOT empiezan con
    // "99", así que un "0" inicial solo puede ser padding).
    let digits = String(code).replace(/[^0-9]/g, "");
    if (digits.length % 2 === 1) digits = `0${digits}`;
    const pairs: number[] = [];
    for (let i = 0; i < digits.length; i += 2) {
      pairs.push(Number(digits.slice(i, i + 2)));
    }
    data = new Uint8Array([0x7b, 0x43, ...pairs]);
  } else {
    const encoder = new TextEncoder();
    data = new Uint8Array([0x7b, 0x42, ...Array.from(encoder.encode(code))]);
  }

  return new Uint8Array([
    GS,
    0x77,
    Math.max(1, Math.min(6, Math.round(barWidth))),
    GS,
    0x68,
    Math.max(16, Math.min(255, Math.round(height))),
    GS,
    0x48,
    textPosition,
    GS,
    0x6b,
    0x49,
    data.length,
    ...Array.from(data),
  ]);
}

export interface QrEscPosOptions {
  /** Tamaño de módulo en dots (1-16; 6 = buen tamaño para 58 mm). */
  moduleSize?: number;
  /** Nivel de corrección: 48=L, 49=M, 50=Q, 51=H (por defecto M). */
  eccLevel?: 48 | 49 | 50 | 51;
}

/**
 * QR Code Model 2 nativo vía `GS ( k` (fn 65/67/69/80/81).
 * Secuencia estándar Epson: modelo → tamaño → ECC → datos → imprimir.
 */
export function buildQrCodeEscPos(
  text: string,
  opts: QrEscPosOptions = {},
): Uint8Array {
  const { moduleSize = 6, eccLevel = 49 } = opts;
  const bytes: number[] = [];

  const gsk = (payload: number[]) => {
    const len = payload.length;
    bytes.push(GS, 0x28, 0x6b, len % 256, Math.floor(len / 256), ...payload);
  };

  // fn 65: seleccionar modelo QR (n1=50 modelo 2, n2=0)
  gsk([0x31, 0x41, 0x32, 0x00]);
  // fn 67: tamaño de módulo (1-16)
  gsk([0x31, 0x43, Math.max(1, Math.min(16, Math.round(moduleSize)))]);
  // fn 69: nivel de corrección de errores
  gsk([0x31, 0x45, eccLevel]);
  // fn 80: almacenar datos — la mayoría de térmicas TRUNCA a 255 bytes por
  // comando, así que se envía en bloques de ≤255 (la impresora acumula y
  // arma el QR completo; antes, QRs con muchos items se cortaban/dañaban).
  const encoder = new TextEncoder();
  const data = Array.from(encoder.encode(text));
  const QR_DATA_CHUNK = 255;
  for (let i = 0; i < data.length; i += QR_DATA_CHUNK) {
    gsk([0x31, 0x50, ...data.slice(i, i + QR_DATA_CHUNK)]);
  }
  // fn 81: imprimir QR (m=48)
  gsk([0x31, 0x51, 0x30]);

  return new Uint8Array(bytes);
}
